/**
 * Where a Pending Conflict is resolved: the summary, the two bodies, and the two
 * buttons that discard one of them (ADR-0002, #7).
 *
 * It never opens itself. The panel opens into the editor whatever the sync state is,
 * because a tray app that greets a quick capture with a decision has stopped being a
 * scratchpad; the Conflict waits behind a badge in `StatusBar` and this opens when
 * that badge is pressed. Deferring it indefinitely is a supported outcome, not a
 * state to nag about.
 *
 * The heavy half is `ConflictDiff`, loaded with `lazy` so that the diff renderer —
 * and Shiki inside it — costs nothing on the launches where nothing conflicts, which
 * is nearly all of them.
 */

import { Suspense, lazy, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { MarkdownEditor } from '@/editor/MarkdownEditor'
import { relativeTime } from '@/lib/format'
import { useResolveConflict, useResolvedTheme, useSettings } from '@/store/app-store'
import type { Resolution } from '@/sync/engine'
import type { ScratchpadRecord } from '@shared/sync-wire'

const ConflictDiff = lazy(() => import('@/components/ConflictDiff'))

export interface ConflictPaneProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The Jot Server's body, as the Pending Conflict recorded it. */
  record: ScratchpadRecord
  /** The editor's live text: the body "keep mine" is about. */
  localBody: string
}

/** Which body the preview is showing, or null while the summary is. */
type Previewing = Resolution | null

const SIDE_LABEL: Record<Resolution, string> = {
  local: 'this device',
  server: 'the server',
}

export function ConflictPane({
  open,
  onOpenChange,
  record,
  localBody,
}: ConflictPaneProps): ReactNode {
  const resolve = useResolveConflict()
  const theme = useResolvedTheme()
  const { settings } = useSettings()

  const [previewing, setPreviewing] = useState<Previewing>(null)
  const [resolving, setResolving] = useState<Resolution | null>(null)

  const busy = resolving !== null

  /**
   * Keep one body, and let the pane close only if the Conflict actually cleared.
   *
   * Which is why the engine's answer is awaited rather than fired and forgotten: a
   * Resolution can come back as a *different* Conflict — the server moved again while
   * this one sat — and closing on the click would hide that behind a badge the user
   * has just been told they dealt with. Anything else that is not an agreement
   * (offline, unauthorised, a failed write) leaves the pane open too, with the badge
   * still reporting why.
   */
  const keep = async (choice: Resolution): Promise<void> => {
    if (busy) return
    setResolving(choice)
    try {
      const status = await resolve(choice)
      if (status.kind === 'agreed') onOpenChange(false)
      else setPreviewing(null)
    } finally {
      setResolving(null)
    }
  }

  const previewBody = previewing === 'server' ? record.body : localBody

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent
        description="The Jot Server holds a different version of the scratchpad. Choose which one to keep; the other is discarded."
        // Nearly the whole panel, unlike Settings: this is a decision made by reading
        // two bodies, and a 520px box inside a 720px window would leave the text it is
        // about in a column narrower than the editor it came from.
        //
        // Grid rows, not the base's flex column: a flex column here left the header and
        // footer's own height fighting the middle row for space whenever the diff below
        // them was tall enough to make the *dialog itself* (not just the diff) the thing
        // that scrolled — and a flex item's `sticky` only holds against a scrolling
        // ancestor while that ancestor's own scrollTop already covers the item's static
        // position, which this dialog's ancestor never reliably did. `grid-rows-[auto_1fr_auto]`
        // has no such failure mode: the header and footer rows are exactly their content's
        // height, the middle row is exactly what's left, and only `ConflictDiff`'s own
        // `overflow-auto` — the actual scrollbar the user sees — ever scrolls, so the
        // buttons in `footer` are never inside a scrolling context in the first place.
        // `overflow-hidden` here isn't optional: without it this grid, like the flex
        // column before it, is still tall enough to make the dialog scroll instead.
        className="grid max-h-[calc(100%-1rem)] w-[calc(100%-1rem)] max-w-none grid-rows-[auto_1fr_auto] overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle>
            {previewing === null ? 'Conflict' : `Previewing ${SIDE_LABEL[previewing]}`}
          </DialogTitle>
          {previewing === null ? null : (
            <Button variant="ghost" onClick={() => setPreviewing(null)} disabled={busy}>
              Back
            </Button>
          )}
        </DialogHeader>

        {previewing === null ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-3">
            <div className="shrink-0 space-y-1.5 text-[12px] text-fg-muted">
              <p>
                The scratchpad changed here and on the Jot Server since the two last agreed, so
                neither is simply newer than the other.
              </p>
              <p>
                {/* `pushedBy` is asserted by whichever device pushed it (ADR-0003), so
                    it is reported as a claim and never as a fact about which device
                    that was. */}
                The server&rsquo;s version was saved {relativeTime(record.updatedAt)} by a
                device calling itself &ldquo;{record.pushedBy}&rdquo;.
              </p>
            </div>

            {/* `flex` here, not just `min-h-0 flex-1`: this div is itself a plain block,
                so without becoming a flex container its one child — `ConflictDiff`'s own
                `flex min-h-0 flex-col` root — has nothing telling it to fill this box
                rather than its own content height. Row-direction stretch (the default)
                gives the child this box's height for free; `[&>*]:min-w-0 [&>*]:flex-1`
                does the same for width, which stretch does not cover on a flex row's own
                main axis — without it the diff shrinks to its longest line's natural
                width instead of filling the pane. */}
            <div className="flex min-h-0 flex-1 [&>*]:min-w-0 [&>*]:flex-1">
              <Suspense fallback={<p className="text-[12px] text-fg-faint">Comparing…</p>}>
                <ConflictDiff serverBody={record.body} localBody={localBody} theme={theme} />
              </Suspense>
            </div>
          </div>
        ) : (
          /* Full-bleed in the real editor, so the body reads exactly as it will once
             kept — same font, same wrapping, same markdown highlighting. Inert,
             because typing into a body that may be about to be discarded is a state
             with no good ending (`MarkdownEditor.readOnly`). */
          <div className="min-h-0 flex-1 overflow-hidden">
            <MarkdownEditor
              value={previewBody}
              onChange={() => {}}
              fontSize={settings.fontSize}
              lineWrap={settings.lineWrap}
              spellCheck={false}
              theme={theme}
              readOnly
            />
          </div>
        )}

        <footer className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-surface-raised px-4 py-2.5">
          <div className="flex items-center gap-1">
            {previewing === null ? (
              <>
                <Button variant="ghost" onClick={() => setPreviewing('local')}>
                  Preview mine
                </Button>
                <Button variant="ghost" onClick={() => setPreviewing('server')}>
                  Preview the server&rsquo;s
                </Button>
              </>
            ) : null}
          </div>

          {/* The word the buttons avoid is "merge": nothing is ever combined
              (ADR-0002), and a label that hinted otherwise would be the one place the
              UI disagreed with what the click does. */}
          <div className="flex items-center gap-1.5">
            <Button onClick={() => void keep('local')} disabled={busy}>
              {resolving === 'local' ? 'Keeping mine…' : 'Keep mine'}
            </Button>
            <Button variant="danger" onClick={() => void keep('server')} disabled={busy}>
              {resolving === 'server' ? 'Keeping the server’s…' : 'Keep the server’s'}
            </Button>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
